import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabaseClient';
import { Calendar, Clock, MapPin, User, X, CheckCircle } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

interface Booking {
  id: string;
  booking_type: string;
  trainer_id: string | null;
  nutritionist_id: string | null;
  start_time: string;
  duration_minutes: number;
  status: string;
  total_amount: number;
  location_address: string | null;
  trainer_name?: string;
  nutritionist_name?: string;
}

export default function BookingsPage() {
  const { profile } = useAuth();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'upcoming' | 'past' | 'cancelled'>('all');

  const loadBookings = useCallback(async () => {
    if (!profile) return;

    try {
      let query = supabase
        .from('home_bookings')
        .select('*')
        .eq('client_id', profile.id)
        .order('start_time', { ascending: false });

      const now = new Date().toISOString();

      if (filter === 'upcoming') {
        query = query.gte('start_time', now).neq('status', 'cancelled');
      } else if (filter === 'past') {
        query = query.lt('start_time', now);
      } else if (filter === 'cancelled') {
        query = query.eq('status', 'cancelled');
      }

      const { data, error } = await query;

      if (error) throw error;

      // Enrich with trainer/nutritionist names
      const enrichedBookings = await Promise.all(
        (data || []).map(async (booking) => {
          if (booking.trainer_id) {
            const { data: trainer } = await supabase
              .from('trainers')
              .select('name')
              .eq('id', booking.trainer_id)
              .single();
            return { ...booking, trainer_name: trainer?.name };
          }
          if (booking.nutritionist_id) {
            const { data: nutritionist } = await supabase
              .from('nutritionists')
              .select('name')
              .eq('id', booking.nutritionist_id)
              .single();
            return { ...booking, nutritionist_name: nutritionist?.name };
          }
          return booking;
        })
      );

      setBookings(enrichedBookings as Booking[]);
    } catch (error) {
      console.error('Error loading bookings:', error);
    } finally {
      setLoading(false);
    }
  }, [profile, filter]);

  useEffect(() => {
    if (profile) {
      loadBookings();
    }
  }, [profile, loadBookings]);

  const handleCancel = async (bookingId: string) => {
    if (!confirm('Are you sure you want to cancel this booking?')) return;

    try {
      const { error } = await supabase
        .from('home_bookings')
        .update({ status: 'cancelled' })
        .eq('id', bookingId);

      if (error) throw error;

      await loadBookings();
    } catch (error) {
      console.error('Error cancelling booking:', error);
      alert('Failed to cancel booking. Please try again.');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-100 text-green-700';
      case 'pending':
        return 'bg-yellow-100 text-yellow-700';
      case 'cancelled':
        return 'bg-red-100 text-red-700';
      case 'completed':
        return 'bg-blue-100 text-blue-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="flex min-h-screen items-center justify-center">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
            <p className="mt-4 text-sm text-gray-600">Loading...</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <main className="min-h-screen bg-gray-50 py-8">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">My Bookings</h1>
            <p className="mt-2 text-gray-600">
              View and manage all your fitness and nutrition bookings
            </p>
          </div>

          {/* Filter Tabs */}
          <div className="mb-6 flex gap-2 border-b border-gray-200">
            {(['all', 'upcoming', 'past', 'cancelled'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setFilter(tab)}
                className={`px-4 py-2 text-sm font-semibold capitalize transition ${
                  filter === tab
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Bookings List */}
          {bookings.length === 0 ? (
            <div className="rounded-xl border border-gray-200 bg-white p-12 text-center">
              <Calendar className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-4 text-lg font-semibold text-gray-900">
                No bookings found
              </h3>
              <p className="mt-2 text-sm text-gray-600">
                {filter === 'all'
                  ? "You don't have any bookings yet. Book your first session!"
                  : `No ${filter} bookings found.`}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {bookings.map((booking) => (
                <div
                  key={booking.id}
                  className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <User className="h-5 w-5 text-gray-400" />
                        <h3 className="text-lg font-semibold text-gray-900">
                          {booking.trainer_name || booking.nutritionist_name || 'Session'}
                        </h3>
                        <span
                          className={`rounded-full px-2 py-1 text-xs font-semibold ${getStatusColor(
                            booking.status
                          )}`}
                        >
                          {booking.status}
                        </span>
                      </div>
                      <p className="mt-1 text-sm text-gray-600">
                        {booking.booking_type === 'trainer'
                          ? 'Personal Training Session'
                          : 'Nutrition Consultation'}
                      </p>
                      <div className="mt-4 flex flex-wrap gap-4 text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4" />
                          {formatDate(booking.start_time)}
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4" />
                          {formatTime(booking.start_time)} ({booking.duration_minutes} min)
                        </div>
                        {booking.location_address && (
                          <div className="flex items-center gap-2">
                            <MapPin className="h-4 w-4" />
                            {booking.location_address}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="ml-4 flex flex-col items-end gap-2">
                      <p className="text-lg font-bold text-gray-900">
                        ${booking.total_amount?.toFixed(2) || '0.00'}
                      </p>
                      {booking.status === 'pending' || booking.status === 'confirmed' ? (
                        <button
                          onClick={() => handleCancel(booking.id)}
                          className="flex items-center gap-1 rounded-lg border border-red-300 bg-white px-3 py-1 text-sm font-semibold text-red-700 hover:bg-red-50"
                        >
                          <X className="h-4 w-4" />
                          Cancel
                        </button>
                      ) : booking.status === 'completed' ? (
                        <div className="flex items-center gap-1 text-sm text-green-700">
                          <CheckCircle className="h-4 w-4" />
                          Completed
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}
